import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from 'app/authentication/auth/auth.service';
import { IptvClientFlagsService } from 'app/authentication/auth/iptv-client-flags.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private _refreshInFlight$: Observable<any> | null = null;

    constructor(
        private _authService: AuthService,
        private _iptvFlags: IptvClientFlagsService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip session endpoints (sign-in, refresh-token, sign-out …) to avoid
        // circular refresh loops — those requests must pass through as-is.
        if (req.url.includes('/api/authentication/')) {
            return next.handle(req);
        }

        // No token: let request pass. Guard/navigation logic handles unauthenticated routes.
        if (!this._authService.accessToken) {
            return next.handle(req);
        }

        // Healthy token: do not refresh on every request.
        if (!this._authService.isTokenExpired) {
            return next.handle(req);
        }

        // Ensure the token is fresh before forwarding.
        return this._refreshTokenOnce().pipe(
            switchMap(() => next.handle(req)),
            catchError((error) => {
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    // Token is no longer valid — clear the session.
                    // Route guards will redirect to /auth/sign-in on the next navigation.
                    this._authService.signOut().subscribe();
                }
                return throwError(() => error);
            })
        );
    }

    private _refreshTokenOnce(): Observable<any> {
        if (!this._refreshInFlight$) {
            this._refreshInFlight$ = this._authService.signInUsingToken().pipe(
                shareReplay(1),
                finalize(() => {
                    this._refreshInFlight$ = null;
                })
            );
        }

        return this._refreshInFlight$;
    }
}
